import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Heart } from "lucide-react";
import api from "../lib/api";

const POLL_MS = 2000;
const MAX_POLLS = 12;

export default function PaymentReturn({ cancelled = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (cancelled) return;
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setDetail(data); setState("success"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) { /* keep polling */ }
      tries += 1;
      if (tries >= MAX_POLLS) { setState("timeout"); return; }
      timer = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => clearTimeout(timer);
  }, [cancelled, location.search]);

  const fmt = (amt, cur) => {
    if (amt == null) return "";
    const sym = cur === "sgd" ? "S$" : "$";
    return `${sym}${(amt / 100).toFixed(0)}`;
  };

  return (
    <div className="min-h-screen bg-canvas text-ink flex items-center justify-center p-6">
      <div className="max-w-md w-full rounded-2xl border border-line bg-surface p-8 text-center af-fade-up" data-testid="payment-return">
        {state === "checking" && (
          <>
            <Loader2 className="w-10 h-10 text-brand animate-spin mx-auto" />
            <h1 className="font-display text-xl font-bold mt-5">Confirming your payment…</h1>
            <p className="text-body mt-1 text-sm">Just a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="w-16 h-16 rounded-full bg-brand-soft border border-brand/30 flex items-center justify-center mx-auto">
              <Heart className="w-8 h-8 text-brand" />
            </div>
            <h1 className="font-display text-2xl font-bold mt-5" data-testid="payment-success-heading">Thank you! {fmt(detail?.amount, detail?.currency)} 🙏</h1>
            <p className="text-body mt-2 text-sm">Your support keeps GetInterviewed free for every job seeker. It genuinely means a lot.</p>
          </>
        )}
        {(state === "cancelled" || state === "timeout" || state === "error") && (
          <>
            <div className="w-16 h-16 rounded-full bg-subtle border border-line flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-body" />
            </div>
            <h1 className="font-display text-xl font-bold mt-5" data-testid="payment-cancel-heading">
              {state === "cancelled" ? "Payment cancelled" : state === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="text-body mt-2 text-sm">
              {state === "cancelled" ? "No worries — you can support us any time." : "If you were charged, it'll reflect shortly. No charge was made otherwise."}
            </p>
          </>
        )}
        {state !== "checking" && (
          <button
            data-testid="payment-back-button"
            onClick={() => navigate("/dashboard")}
            className="mt-6 w-full py-2.5 rounded-full bg-brand text-onbrand font-semibold hover:bg-brand-hover transition-colors"
          >
            Back to GetInterviewed
          </button>
        )}
      </div>
    </div>
  );
}
